/**
 * @fileoverview Centralized workspace detection hook
 *
 * This hook provides consistent workspace detection logic across all components
 * to solve the production bug where users with 1 workspace were getting hidden UI elements.
 *
 * **Business Logic:**
 * - 0 workspaces: Show onboarding/empty states
 * - 1 workspace: Show workspace UI (user has access to workspace features)
 * - 2+ workspaces: Show workspace UI + workspace switcher
 *
 * @module hooks/useWorkspaceDetection
 */

import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useAuth } from '@/contexts/AuthProvider';

/**
 * Workspace detection result interface
 */
interface WorkspaceDetectionResult {
  /** Whether user has any workspaces (>= 1) */
  hasWorkspaces: boolean;
  /** Whether user has multiple workspaces (>= 2) */
  hasMultipleWorkspaces: boolean;
  /** Exact number of workspaces */
  workspaceCount: number;
  /** Whether workspace UI elements should be shown */
  shouldShowWorkspaceUI: boolean;
  /** Whether workspace switcher should be shown */
  shouldShowWorkspaceSwitcher: boolean;
  /** Whether organization settings should be shown */
  shouldShowOrganizationSettings: boolean;
  /** Loading state from WorkspaceProvider */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
}

/**
 * Centralized workspace detection hook
 *
 * Provides consistent workspace detection logic across all components.
 * This hook replaces scattered `workspaces.length > 1` checks that were causing
 * the production bug where users with 1 workspace got hidden UI elements.
 *
 * @returns {WorkspaceDetectionResult} Workspace detection state and computed properties
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const {
 *     hasWorkspaces,
 *     shouldShowWorkspaceUI,
 *     shouldShowWorkspaceSwitcher
 *   } = useWorkspaceDetection();
 *
 *   return (
 *     <div>
 *       {shouldShowWorkspaceUI && <WorkspaceFeatures />}
 *       {shouldShowWorkspaceSwitcher && <WorkspaceSwitcher />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useWorkspaceDetection(): WorkspaceDetectionResult {
  const workspaceContext = useWorkspace();
  const { isAuthenticated } = useAuth(); // Get isAuthenticated from AuthProvider!

  // Handle loading and unauthenticated states
  if (!workspaceContext) {
    return {
      hasWorkspaces: false,
      hasMultipleWorkspaces: false,
      workspaceCount: 0,
      shouldShowWorkspaceUI: false,
      shouldShowWorkspaceSwitcher: false,
      shouldShowOrganizationSettings: false,
      isLoading: true,
      isAuthenticated: false,
    };
  }

  const { workspaces, hasWorkspaces: contextHasWorkspaces, isLoading } = workspaceContext;

  // Handle edge cases for workspace data
  const workspaceCount = Array.isArray(workspaces) ? workspaces.length : 0;
  const hasWorkspaces = contextHasWorkspaces || false;
  const hasMultipleWorkspaces = workspaceCount > 1;

  // During loading, maintain previous state to prevent UI flashing
  // Only hide workspace UI if we're loading AND have no workspaces yet
  if (isLoading && workspaceCount === 0) {
    return {
      hasWorkspaces: false,
      hasMultipleWorkspaces: false,
      workspaceCount: 0,
      shouldShowWorkspaceUI: false,
      shouldShowWorkspaceSwitcher: false,
      shouldShowOrganizationSettings: false,
      isLoading: true,
      isAuthenticated,
    };
  }

  // For unauthenticated users, hide all workspace UI
  if (!isAuthenticated) {
    return {
      hasWorkspaces: false,
      hasMultipleWorkspaces: false,
      workspaceCount: 0,
      shouldShowWorkspaceUI: false,
      shouldShowWorkspaceSwitcher: false,
      shouldShowOrganizationSettings: false,
      isLoading: false,
      isAuthenticated: false,
    };
  }

  return {
    hasWorkspaces,
    hasMultipleWorkspaces,
    workspaceCount,
    // Show workspace UI if user has any workspaces (>= 1)
    shouldShowWorkspaceUI: hasWorkspaces,
    // Show workspace switcher only if user has multiple workspaces (>= 2)
    shouldShowWorkspaceSwitcher: hasMultipleWorkspaces,
    // Show organization settings if user has any workspaces (>= 1)
    // This fixes the bug where users with 1 workspace couldn't access organization settings
    shouldShowOrganizationSettings: hasWorkspaces,
    isLoading,
    isAuthenticated,
  };
}

/**
 * Workspace detection utility functions
 *
 * These functions can be used in contexts where hooks are not available
 * (e.g., utility functions, event handlers outside components)
 */
const _WorkspaceDetectionUtils = {
  /**
   * Check if user has any workspaces
   */
  hasWorkspaces: (workspaceCount: number): boolean => workspaceCount > 0,

  /**
   * Check if user has multiple workspaces
   */
  hasMultipleWorkspaces: (workspaceCount: number): boolean => workspaceCount > 1,

  /**
   * Determine if workspace UI should be shown
   */
  shouldShowWorkspaceUI: (workspaceCount: number): boolean => workspaceCount > 0,

  /**
   * Determine if workspace switcher should be shown
   */
  shouldShowWorkspaceSwitcher: (workspaceCount: number): boolean => workspaceCount > 1,

  /**
   * Determine if organization settings should be shown
   */
  shouldShowOrganizationSettings: (workspaceCount: number): boolean => workspaceCount > 0,
};
